import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Stack, TextField, Typography, Tooltip} from 'views/components';
import {HelpOutlineOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import moment from 'moment-timezone';

const DomainGuideOutreachHours = (props) => {
  const {
    domainGuideData,
    updateOutreachHours,
  } = props;

  const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

  const startTime = domainGuideData.outreachHours?.start;
  const endTime = domainGuideData.outreachHours?.end;
  const timezone = domainGuideData.outreachHours?.timeZone; // UTC in backend

  const loadedConvertedStartTime = TIME_PATTERN.test(startTime) ? moment.tz(startTime, 'HH:mm', timezone).tz('America/New_York').format('HH:mm') : '';
  const loadedConvertedEndTime = TIME_PATTERN.test(endTime) ? moment.tz(endTime, 'HH:mm', timezone).tz('America/New_York').format('HH:mm') : '';

  const [convertedStartTime, setConvertedStartTime] = React.useState(loadedConvertedStartTime);
  const [convertedEndTime, setConvertedEndTime] = React.useState(loadedConvertedEndTime);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setConvertedStartTime(loadedConvertedStartTime);
    setConvertedEndTime(loadedConvertedEndTime);
  }, [loadedConvertedEndTime, loadedConvertedStartTime]);

  const canSave = TIME_PATTERN.test(convertedStartTime) && TIME_PATTERN.test(convertedEndTime);
  const hasChanges = convertedStartTime !== loadedConvertedStartTime || convertedEndTime !== loadedConvertedEndTime;

  const saveOutreachHours = async () => {
    if (!canSave || !hasChanges || isSaving) {
      return;
    }

    setIsSaving(true);

    await updateOutreachHours({
      start: moment.tz(convertedStartTime, 'HH:mm', 'America/New_York').utc().format('HH:mm'),
      end: moment.tz(convertedEndTime, 'HH:mm', 'America/New_York').utc().format('HH:mm'),
      timeZone: 'UTC',
    });

    setIsSaving(false);
  };

  const clearOutreachHours = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    await updateOutreachHours(null);

    setConvertedStartTime('');
    setConvertedEndTime('');
    setIsSaving(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E8E4DE',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Stack sx={{gap: 2, px: 3, py: 2}}>
        <Stack sx={{gap: '4px'}}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '11px',
              lineHeight: '14px',
              color: '#A8A49E',
            }}
          >
            <Translate expression='domainGuide.Detail.OutreachHours.Title' />
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: .5}}>
            <Typography sx={{fontSize: '12px', lineHeight: '16px', color: '#6B6761'}}>
              <Translate expression='domainGuide.Detail.OutreachHours.Subtitle' />
            </Typography>
            <Tooltip title={translate('domainGuide.Detail.OutreachHours.Help')}>
              <HelpOutlineOutlined
                sx={{fontSize: '14px', lineHeight: '16px', color: '#6B6761'}}
              />
            </Tooltip>
          </Box>
        </Stack>
        <Stack
          direction={{xs: 'column', sm: 'row'}}
          sx={{gap: 2}}
        >
          <Stack sx={{gap: '8px', flex: 1}}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '11px',
                lineHeight: '14px',
                color: '#A8A49E',
              }}
            >
              <Translate expression='domainGuide.Detail.OutreachHours.StartLabel' />
            </Typography>
            <TextField
              fullWidth
              inputProps={{step: 60}}
              type='time'
              value={convertedStartTime}
              onChange={setConvertedStartTime}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                  fontSize: '14px',
                },
              }}
            />
          </Stack>
          <Stack sx={{gap: '8px', flex: 1}}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '11px',
                lineHeight: '14px',
                color: '#A8A49E',
              }}
            >
              <Translate expression='domainGuide.Detail.OutreachHours.EndLabel' />
            </Typography>
            <TextField
              fullWidth
              inputProps={{step: 60}}
              type='time'
              value={convertedEndTime}
              onChange={setConvertedEndTime}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                  fontSize: '14px',
                },
              }}
            />
          </Stack>
        </Stack>
        <Stack direction='row' sx={{gap: 2, alignItems: 'center'}}>
          <Button
            disabled={!canSave || isSaving || !hasChanges}
            onClick={saveOutreachHours}
            sx={{
              'px': '16px',
              'py': '8px',
              'borderRadius': '8px',
              'backgroundColor': '#DA7955',
              'boxShadow': 'none',
              'color': '#FFFFFF',
              'fontSize': '13px',
              'fontWeight': 700,
              'textTransform': 'none',
              '&:hover': {
                backgroundColor: '#C4512C',
                boxShadow: 'none',
              },
            }}
            variant='contained'
          >
            {
              isSaving ?
                translate('domainGuide.Detail.OutreachHours.Saving') :
                translate('domainGuide.Detail.OutreachHours.Save')
            }
          </Button>
          <Button
            disabled={isSaving}
            onClick={clearOutreachHours}
            sx={{
              'px': '16px',
              'py': '8px',
              'ml': 'auto',
              'border': '1px solid #E8E4DE',
              'borderRadius': '8px',
              'backgroundColor': '#FFFFFF',
              'boxShadow': 'none',
              'color': '#1A1916',
              'fontSize': '13px',
              'fontWeight': 700,
              'textTransform': 'none',
              '&:hover': {
                borderColor: '#D8D2CA',
                backgroundColor: '#F8F6F3',
                boxShadow: 'none',
              },
            }}
            variant='outlined'
          >
            <Translate expression='domainGuide.Detail.OutreachHours.Clear' />
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

DomainGuideOutreachHours.propTypes = {
  domainGuideData: PropTypes.object.isRequired,
  updateOutreachHours: PropTypes.func.isRequired,
};

export default React.memo(DomainGuideOutreachHours);
